import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { methods, isConnectionError, isUsingBackendProxy, canUseBackendProxy, enableBackendProxy } from "../../../utils/axios";
import { installGlobalErrorReporting, reportClientError, setErrorContext } from "../../../utils/error-reporter";
import { TestTypeService } from "../../../utils/test-type-registry";
import DiscTest from "./disc-test";
import CompetenceTest from "./competence-test";
import UnifiedTest from "./unified-test";
import TestErrorBoundary from "./test-error-boundary";

// Sin límite, un pedido que la red deja colgado (firewall de empresa) dejaba la
// pantalla en "Cargando..." para siempre.
const LOAD_TIMEOUT_MS = 20000;

// La página del test habla con el back por el proxy del mismo dominio del front
// (app.growmeapp.ai/_backend): un solo dominio, el mismo que la red ya dejó abrir.
// Antes iba directo a superadmin-back.growmeapp.ai, que las redes de empresa no
// conocen y a veces bloqueaban. La vía directa queda de respaldo (proxyFallback).
const loadTest = (collaboratorTestID) =>
  methods.post('/collaborator_tests', { collaboratorTestID }, { timeout: LOAD_TIMEOUT_MS, proxyFallback: true });

const Test = () => {

  const [test, setTest] = useState();
  const [loadFailed, setLoadFailed] = useState(false);
  const [connectionFailed, setConnectionFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [searchParams] = useSearchParams();
  const collaboratorTestID = searchParams.get('id');

  useEffect(() => {
    installGlobalErrorReporting();
    setErrorContext({ collaboratorTestID });
  }, [collaboratorTestID]);

  // Solo en la página del test (no en el panel): todo por el proxy del front.
  // useState para que corra una vez y antes del primer pedido.
  useState(() => {
    if (canUseBackendProxy()) enableBackendProxy();
  });

  useEffect(() => {
    let cancelled = false;
    setLoadFailed(false);
    setConnectionFailed(false);
    if (!collaboratorTestID) {
      setLoadFailed(true);
      return;
    }
    loadTest(collaboratorTestID)
      .then((res) => {
        if (cancelled) return;
        if (canUseBackendProxy() && !isUsingBackendProxy()) {
          // No es un error del candidato: el proxy del front no respondió y se usó la
          // vía directa. Sirve para detectar si el proxy de Vercel tiene problemas.
          reportClientError('direct_fallback', 'El test abrió por la vía directa: el proxy no respondió', {}, 'info');
        }
        if (res.data) {
          setTest({ ...res.data, collaboratorTestID });
        } else {
          setLoadFailed(true);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        console.log(error);
        // Un 4xx es un link inválido o inexistente (bots que prueban IDs inventados),
        // no una falla de la plataforma: se registra como info para no disparar la alarma.
        const status = error?.response?.status;
        const invalidLink = status >= 400 && status < 500;
        reportClientError('test_load_failed', error, {}, invalidLink ? 'info' : 'error');
        if (isConnectionError(error)) {
          setConnectionFailed(true);
        } else {
          // 404: el test ya no existe (p. ej. se borró y le enviaron uno nuevo; back #95).
          setLoadFailed(status === 404 ? 'not_found' : true);
        }
      });
    return () => { cancelled = true; };
  }, [collaboratorTestID, attempt]);

  const getTest = () => {
    if (connectionFailed) {
      return (
        <div className="test-loading test-load-error">
          <h1>No pudimos conectarnos</h1>
          <p>
            Tu conexión no logró comunicarse con nuestra plataforma. Esto suele pasar
            en redes de empresa, que bloquean algunos sitios.
          </p>
          <ul>
            <li>Prueba abrir el link desde tu celular usando datos móviles (no el wifi de la empresa).</li>
            <li>O conéctate a otra red, como la de tu casa.</li>
            <li>Si usas VPN, desactívala y vuelve a intentar.</li>
            <li>También puedes probar con otro navegador (Chrome, Edge, Safari o Firefox).</li>
          </ul>
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>
            Reintentar
          </button>
          <p>
            Si el problema continúa, contacta a la persona que te envió la prueba.
          </p>
        </div>
      );
    }

    // Antes, si la carga fallaba (link cortado, test eliminado, back caído) la
    // pantalla quedaba en "Cargando..." para siempre.
    if (loadFailed === 'not_found') {
      return (
        <div className="test-loading test-load-error">
          <h1>Este link ya no es válido</h1>
          <p>
            Es posible que te hayan enviado una invitación nueva. Busca en tu correo el
            mensaje más reciente con tu Test GrowMe y entra desde ese link.
          </p>
          <p>
            Si no lo encuentras, contacta a la persona que te envió la prueba.
          </p>
        </div>
      );
    }

    if (loadFailed) {
      return (
        <div className="test-loading test-load-error">
          <h1>No pudimos cargar la prueba</h1>
          <p>
            Es posible que el link esté incompleto o que ya no sea válido.
            Cierra esta pestaña y vuelve a entrar desde el link del correo que recibiste.
          </p>
          <p>
            Si el problema continúa, contacta a la persona que te envió la prueba.
          </p>
        </div>
      );
    }

    if (!test) {
      return (
        <div className="test-loading">
          <h1>Cargando...</h1>
        </div>
      );
    }

    if (test.results) {
      return (
        <div className="test-loading">
          <h1>La prueba ya fue respondida</h1>
        </div>
      );
    }

    const config = TestTypeService.getTestConfigByTest(test);

    if (config?.category === 'competency') {
      return <CompetenceTest test={test} />;
    }

    if (config?.category === 'unified') {
      return <UnifiedTest test={test} />;
    }

    if (config?.category === 'disc' || config?.category === 'bounded-disc') {
      return <DiscTest test={test} isBoundedTest={config.category === 'bounded-disc'} />;
    }

    return (
      <div className="test-loading">
        <h1>Tipo de test no manejado</h1>
      </div>
    );
  };

  return (
    <TestErrorBoundary>
      {getTest()}
    </TestErrorBoundary>
  );
}

export default Test;